import { ref } from "vue";

import { defineStore } from "pinia";

import { usePromiseWrapper } from "@/hooks/common";
import twilioApiService from "@/services/modules/twilio";

import type { IPaginatedResponse, ParamRecord } from "@/models/common";
import type {
  TwilioAssignmentPayload,
  TwilioPhoneNumber,
} from "@/models/twilio";

export const useTwilioStore = defineStore("twilio", () => {
  const availablePhoneNumbers =
    ref<IPaginatedResponse<TwilioPhoneNumber> | null>(null);
  const userAssignedPhoneNumbers = ref<TwilioPhoneNumber[] | null>(null);
  const refetchUserAssignedPhoneNumbers = ref(false);

  const {
    fetchWrapper: getAvailableNumbers,
    loading: loadingAvailableNumbers,
  } = usePromiseWrapper(async (params: ParamRecord) => {
    const response = await twilioApiService.getAvailablePhoneNumbers(params);
    availablePhoneNumbers.value = response;
  });

  const {
    fetchWrapper: getUserAssignedTwilioNumbers,
    loading: loadingAssignedNumbers,
  } = usePromiseWrapper(async (userId: number) => {
    const response =
      await twilioApiService.getUserAssignedTwilioPhoneNumbers(userId);
    userAssignedPhoneNumbers.value = response.data;
  });

  const { fetchWrapper: saveUserAssignedNumbers, loading: savingAssignment } =
    usePromiseWrapper(async (payload: TwilioAssignmentPayload) => {
      await twilioApiService.updateUserAssignedPhoneNumbers(payload);
      refetchUserAssignedPhoneNumbers.value = true;
    });

  return {
    availablePhoneNumbers,
    userAssignedPhoneNumbers,
    loadingAvailableNumbers,
    loadingAssignedNumbers,
    savingAssignment,
    refetchUserAssignedPhoneNumbers,
    getAvailableNumbers,
    getUserAssignedTwilioNumbers,
    saveUserAssignedNumbers,
  };
});
